import type { SortDescriptor } from "react-stately";

import { memo, useState } from "react";

import type { RequestFilterType } from "../../../types/filters";

import { activeTableConfig } from "../../../config/tables/customer/activeTableConfig";
import { useActiveRequests } from "../../../hooks";
import {
  useRequestRowActions,
  useResetFilterOnEmptySearch,
} from "../../../hooks/shared";
import { BulkDuplicateModal } from "../../modals/BulkDuplicateModal";
import { ConfigurableTable } from "../../tables";

interface ActiveTabProps {
  onFilterChange?: (filter: RequestFilterType) => void;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
}

function ActiveTabComponent({
  onFilterChange,
  searchValue,
  selectedFilter,
}: ActiveTabProps) {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: activeTableConfig.defaultSort?.column || "createdAt",
    direction:
      activeTableConfig.defaultSort?.direction === "desc"
        ? "descending"
        : "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { customProps, duplicateModalProps } = useRequestRowActions();

  const {
    data: requestsData,
    isFetching,
    isLoading,
    isSuccess,
  } = useActiveRequests({
    filter: selectedFilter,
    limit: itemsPerPage,
    page: currentPage,
    search: searchValue,
    sortDescriptor,
  });

  useResetFilterOnEmptySearch({
    // Never the broadest scope ("All" tab is), so always escalate.
    isAlreadyBroadest: false,
    isReady: isSuccess && !isFetching,
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: requestsData?.total ?? 0,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const activeRequests = requestsData?.requests || [];

  return (
    <>
      <ConfigurableTable
        config={activeTableConfig}
        customProps={customProps}
        data={activeRequests}
        isLoading={isLoading}
        paginationProps={{
          metadata: {
            currentPage,
            itemsPerPage,
            totalItems: requestsData?.total ?? null,
            totalPages: requestsData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handleItemsPerPageChange,
          onPageChange: handlePageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        setSortDescriptor={setSortDescriptor}
        sortDescriptor={sortDescriptor}
        useAdvancedSorting={true}
      />

      <BulkDuplicateModal {...duplicateModalProps} />
    </>
  );
}

export const ActiveTab = memo(ActiveTabComponent);
